import { createApi } from '@reduxjs/toolkit/query/react';
import { Endpoint } from 'services/endpoints';

import { Asset } from 'types/assets';
import { Country } from 'types/country';
import { Response } from 'types/http';

import { fetchBaseQueryWithAuth } from '../api';
import { AssetsListResponse, CountriesListResponse, FiatAssetsResponse, PWCAssetsResponse } from './types';

const REDUCER_PATH = 'assetsApi';

export const BridgeRiskClassification = ['Prohibited', 'Controlled', 'Restricted', 'NotHighRisk'] as const;
export type BridgeRiskClassificationType = (typeof BridgeRiskClassification)[number];

export const assetsApi = createApi({
  reducerPath: REDUCER_PATH,
  baseQuery: fetchBaseQueryWithAuth,
  endpoints: (builder) => ({
    getAssetList: builder.query<Asset[], void>({
      query: () => Endpoint.Assets.List(false),
      transformResponse: (response: Response<AssetsListResponse>) => {
        if (!response?.data?.assets) {
          return [];
        }

        return response.data?.assets;
      },
    }),
    getActiveAssetList: builder.query<Asset[], void>({
      query: () => Endpoint.Assets.List(true),
      transformResponse: (response: Response<AssetsListResponse>) => {
        if (!response?.data?.assets) {
          return [];
        }

        return response.data?.assets;
      },
    }),
    getPWCFiatAssetList: builder.query<FiatAssetsResponse, void>({
      query: () => Endpoint.PwcAssets.FiatList,
      transformResponse: (response: Response<FiatAssetsResponse>) => response?.data ?? { assets: [] },
    }),
    getPWCAssetList: builder.query<PWCAssetsResponse, string | undefined>({
      query: (merchantUuid) => Endpoint.PwcAssets.List(merchantUuid),
      transformResponse: (response: Response<PWCAssetsResponse>) => {
        if (!response?.data) {
          return {
            assets: [],
          };
        }

        return response.data;
      },
    }),
    getCountriesList: builder.query<Country[], BridgeRiskClassificationType | BridgeRiskClassificationType[] | void>({
      query: (riskClassification) => {
        const baseUrl = Endpoint.Assets.CountriesList;
        if (!riskClassification) return baseUrl;

        const params = Array.isArray(riskClassification)
          ? riskClassification.map((rc) => `riskClassification=${rc}`).join('&')
          : `riskClassification=${riskClassification}`;

        return `${baseUrl}?${params}`;
      },
      transformResponse: (response: Response<CountriesListResponse>) => {
        if (!response?.data?.countries) {
          return [];
        }

        return response.data.countries;
      },
    }),
  }),
});

export const { getAssetList, getCountriesList } = assetsApi.endpoints;
export const {
  useGetAssetListQuery,
  useGetActiveAssetListQuery,
  useGetCountriesListQuery,
  useGetPWCAssetListQuery,
  useGetPWCFiatAssetListQuery,
} = assetsApi;
export default assetsApi;
